import React, { useMemo } from "react";
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
} from "@tanstack/react-table";
import WaterMark from "../../Components/WaterMark/WaterMark";

const DealerReviewTable = React.memo(({ name, data = [] }) => {
  const safeData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const totalRow = useMemo(() => {
    if (safeData.length === 0) return { LOB: "Total" };

    const total = { LOB: "Total" };

    safeData.forEach((row) => {
      if (!row || typeof row !== "object") return;

      Object.keys(row).forEach((key) => {
        if (typeof row[key] === "object" && row[key] !== null) {
          total[key] = total[key] || {};

          Object.keys(row[key]).forEach((nestedKey) => {
            if (typeof row[key][nestedKey] === "number") {
              total[key][nestedKey] =
                (total[key][nestedKey] || 0) + row[key][nestedKey];
              total[key][nestedKey] = parseFloat(
                total[key][nestedKey].toFixed(2),
              );
            }
          });
        }
      });
    });

    if (total.month_performance) {
      const keys = Object.keys(total.month_performance).filter(
        (key) => key !== "variance",
      );
      const firstKey = keys[0];
      const secondKey = keys[1];

      const PrevYear = parseFloat(total.month_performance[firstKey]);
      const CurrentYear = parseFloat(total.month_performance[secondKey]);

      if (!isNaN(PrevYear) && !isNaN(CurrentYear)) {
        if (name === "Realization as % of list price") {
          total.month_performance.variance = parseFloat(
            (CurrentYear - PrevYear).toFixed(2),
          );
        } else {
          total.month_performance.variance = parseFloat(
            (((CurrentYear - PrevYear) / PrevYear) * 100).toFixed(2),
          );
        }
      }
    }

    if (total.ytd_performance) {
      const keys = Object.keys(total.ytd_performance);
      const firstKey = keys[0];
      const secondKey = keys[1];

      const ytdPrev = parseFloat(total.ytd_performance[firstKey]);
      const ytdCurrent = parseFloat(total.ytd_performance[secondKey]);

      if (!isNaN(ytdPrev) && !isNaN(ytdCurrent)) {
        if (name === "Realization as % of list price") {
          total.ytd_performance.variance = parseFloat(
            (ytdCurrent - ytdPrev).toFixed(2),
          );
        } else {
          total.ytd_performance.variance = parseFloat(
            (((ytdCurrent - ytdPrev) / ytdPrev) * 100).toFixed(2),
          );
        }
      }
    }

    if (name === "Realization as % of list price") {
      Object.keys(total).forEach((key) => {
        if (typeof total[key] === "object") {
          Object.keys(total[key]).forEach((nestedKey) => {
            if (typeof total[key][nestedKey] === "number") {
              total[key][nestedKey] = `${parseFloat(
                (total[key][nestedKey] / 4).toFixed(2),
              )}`;
            }
          });
        }
      });
    }

    return total;
  }, [safeData, name]);

  const columns = useMemo(() => {
    if (safeData.length === 0) return [];

    const firstRow = safeData[0];
    let columnIndex = -1;

    const generateColumns = (obj, parentKey = "") => {
      return Object.keys(obj).map((key) => {
        columnIndex += 1;

        const accessorKey = parentKey ? `${parentKey}.${key}` : key;
        let header = key.replace(/_/g, " ").toLowerCase();
        header = header.replace(/\b\w/g, (char) => char.toUpperCase());

        const getColumnSize = (index) => {
          if (index === 0) return 40;
          if (index === 1) return 150;
          if (index === 2) return 200;
          if (index === 3) return 150;
          return 100;
        };

        if (typeof obj[key] === "object" && obj[key] !== null) {
          return {
            id: key,
            header,
            columns: generateColumns(obj[key], accessorKey),
          };
        }

        return {
          id: accessorKey,
          accessorKey,
          size: getColumnSize(columnIndex),
          header: () => header,

          cell: ({ getValue, column, row }) => {
            const value = getValue();

            // First column stays plain
            if (column.getIndex() === 0) {
              return value != null ? value : "-";
            }

            // ✅ Handle month_performance[1] coloring
            if (accessorKey.startsWith("month_performance.")) {
              const keys = Object.keys(
                row.original.month_performance || {},
              ).filter((k) => k !== "variance");
              if (keys.length >= 2) {
                const prev = row.original.month_performance[keys[0]];
                const current = row.original.month_performance[keys[1]];

                if (accessorKey === `month_performance.${keys[1]}`) {
                  const isPositive = current > prev;
                  const textColor = isPositive
                    ? "text-green-600"
                    : "text-red-600";

                  return (
                    <span className={textColor}>
                      {value != null ? value : "-"}
                      {name === "Realization as % of list price" ? "%" : ""}
                    </span>
                  );
                }
              }
            }

            // ✅ Handle ytd_performance[1] coloring
            if (accessorKey.startsWith("ytd_performance.")) {
              const keys = Object.keys(
                row.original.ytd_performance || {},
              ).filter((k) => k !== "variance");
              if (keys.length >= 2) {
                const prev = row.original.ytd_performance[keys[0]];
                const current = row.original.ytd_performance[keys[1]];

                if (accessorKey === `ytd_performance.${keys[1]}`) {
                  const isPositive = current > prev;
                  const textColor = isPositive
                    ? "text-green-600"
                    : "text-red-600";

                  return (
                    <span className={textColor}>
                      {value != null ? value : "-"}
                      {name === "Realization as % of list price" ? "%" : ""}
                    </span>
                  );
                }
              }
            }

            // ✅ Variance columns keep % format
            if (
              accessorKey === "month_performance.variance" ||
              accessorKey === "ytd_performance.variance" ||
              name === "Realization as % of list price"
            ) {
              return value != null ? `${value}%` : "-";
            }

            // Default
            return value != null ? value : "-";
          },
        };
      });
    };

    return generateColumns(firstRow);
  }, [safeData, name]);

  const table = useReactTable({
    data: useMemo(() => [...safeData, totalRow], [safeData, totalRow]),
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const getColWidthClass = (index) => {
    if (index === 0) return "w-[40px]";
    if (index === 1) return "w-[150px]";
    if (index === 2) return "w-[200px]";
    if (index === 3) return "w-[150px]";
    return "w-[100px]";
  };

  return (
    <div className="bg-white shadow-lg text-xs rounded-t-lg w-full relative h-full mt-2 md:mt-0">
      <WaterMark />
      <h2 className="md:text-lg text-[16px] font-bold text-gray-800 p-2 mb-1">
        {name}
      </h2>
      <div className="w-full overflow-x-auto drilltable ">
        <table className="min-w-[410px] border border-gray-300 w-full mt-[2px]  table-fixed">
          <thead className="bg-[#28526E] text-white">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, i) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className={`py-1 px-[3px] border text-center text-xs ${getColWidthClass(
                      i,
                    )}`}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="text-gray-700 text-center">
            {table.getRowModel().rows.map((row, rowIndex) => (
              <tr key={row.id} className="border">
                {row.getVisibleCells().map((cell, cellIndex) => {
                  let cellClass = getColWidthClass(cellIndex);

                  if (
                    [0, 2, 4].includes(rowIndex) &&
                    cellIndex >= 1 &&
                    cellIndex <= 11
                  ) {
                    cellClass = " bg-[#EEF0F5]";
                  }
                  if (cellIndex === 0) {
                    cellClass = " bg-[#D3DDE6]";
                  }
                  if (row.original.LOB === "Total") {
                    cellClass = " bg-[#D3DDE6]  font-bold  ";
                  }

                  return (
                    <td
                      key={cell.id}
                      className={`py-[6px] border border-[#B9B9B9] text-center ${cellClass}`}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default DealerReviewTable;
